import { useState } from 'react'
import { apiService } from '../services/api'
import { mensajesDeValidacion } from '@/features/rh/lib/mensajeDeValidacion'
import type { 
  JobRequest, 
  ApproveJobRequestDto, 
  RejectJobRequestDto, 
  ReturnJobRequestDto, 
  RespondToReturnedRequestDto 
} from '../services/api'

export const useJobRequestActions = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const executeAction = async <T>(
    action: () => Promise<T>
  ): Promise<T | null> => {
    try {
      setLoading(true)
      setError(null)
      const result = await action()
      return result
    } catch (errCrudo: unknown) {
      // El servidor valida con class-validator y sus mensajes vienen en
      // ingles y en arreglo. Sin traducirlos, al usuario le llegaba algo como
      // «comentariosHr must be a string» pegado con los demas renglones.
      setError(mensajesDeValidacion(errCrudo, 'Ha ocurrido un error'))
      return null
    } finally {
      setLoading(false)
    }
  }

  const reviewRequest = async (id: string): Promise<JobRequest | null> => {
    return executeAction(() => apiService.reviewJobRequest(id))
  }

  const approveRequest = async (id: string, comentarios?: string): Promise<JobRequest | null> => {
    const data: ApproveJobRequestDto = { comentariosHr: comentarios }
    return executeAction(() => apiService.approveJobRequest(id, data))
  }

  const rejectRequest = async (
    id: string,
    comentariosHr: string,
  ): Promise<JobRequest | null> => {
    const data: RejectJobRequestDto = { comentariosHr }
    return executeAction(() => apiService.rejectJobRequest(id, data))
  }

  /**
   * Devuelve al flujo una solicitud rechazada (SRH-317). El motivo es
   * opcional: lo normal es deshacer un dedazo, y exigir explicación para eso
   * solo invita a escribir «error» y seguir.
   */
  const reactivateRequest = async (
    id: string,
    motivo?: string,
  ): Promise<JobRequest | null> => {
    return executeAction(() =>
      apiService.reactivateJobRequest(id, motivo ? { motivo } : {}),
    )
  }

  const returnRequest = async (
    id: string, 
    comentariosHr: string, 
    informacionAdicionalSolicitada?: string
  ): Promise<JobRequest | null> => {
    const data: ReturnJobRequestDto = { 
      comentariosHr,
      informacionAdicionalSolicitada
    }
    return executeAction(() => apiService.returnJobRequest(id, data))
  }

  const respondToReturned = async (
    id: string, 
    respuesta?: string,
    updatedData?: Partial<RespondToReturnedRequestDto>
  ): Promise<JobRequest | null> => {
    const data: RespondToReturnedRequestDto = {
      respuestaSolicitante: respuesta,
      ...updatedData
    }
    return executeAction(() => apiService.respondToReturnedRequest(id, data))
  }

  return {
    loading,
    error,
    reviewRequest,
    approveRequest,
    rejectRequest,
    returnRequest,
    reactivateRequest,
    respondToReturned,
    clearError: () => setError(null)
  }
}
